import { useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router'
import { FormContextValues } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { getCitiesList } from '../services/Kyc'
import {
  asyncDebounce,
  handleError,
  processCitiesList,
} from '../services/utils'
import { STATUS } from '../enums/common'
import { SELECT_FIRST_OPTION } from '../components/step4/Options'
import { ICommuneData } from '../interfaces/Address'
import { IAddressCityOption } from '../interfaces/Step4'
import { IStore } from '../interfaces/Store'
import { citiesActions } from '../store/slices/Cities'

const useCityListHook = (
  city: string,
  watch: FormContextValues['watch'],
  setError: FormContextValues['setError'],
  clearError: FormContextValues['clearError']
): [string, IAddressCityOption[]] => {
  const dispatch = useDispatch()
  const { status } = useSelector((state: IStore) => state.citiesReducer)
  const [citiesList, setCitiesList] = useState<IAddressCityOption[]>([
    SELECT_FIRST_OPTION,
  ])
  const history = useHistory()
  const postalCode = watch('adresse_cp')

  const getCities = useMemo(() => {
    return asyncDebounce(
      (cityValue: string, postalCodeValue: string) =>
        getCitiesList(cityValue, postalCodeValue),
      1000
    )
  }, [])

  useEffect(() => {
    async function getCityList() {
      dispatch(citiesActions.setStatus({ status: STATUS.pending }))
      try {
        const response = (await getCities(city, postalCode)) as ICommuneData[]

        setCitiesList([
          SELECT_FIRST_OPTION,
          ...processCitiesList(response),
        ] as IAddressCityOption[])
        dispatch(citiesActions.setStatus({ status: STATUS.loaded }))

        if (
          postalCode &&
          !watch('commune_habitation').value &&
          !response.some((c) => c.cadpo === postalCode)
        ) {
          setError(
            'commune_habitation',
            'noOptions',
            "Il n'y a pas de commune d'habitation correspondant à ce code postal"
          )
        } else {
          clearError('commune_habitation')
        }
      } catch (e) {
        dispatch(citiesActions.setStatus({ status: STATUS.failed }))
        handleError(e, history)
      }
    }

    getCityList()
    // eslint-disable-next-line
  }, [city, postalCode])

  return [status, citiesList]
}

export default useCityListHook
